import {React, observer, computed, IDisposer, useEffect, classNames, useAsObservableSource, Popover2, useState, computedFn} from '@api'
import {DatePicker, DateRange, DateRangePicker} from '@blueprintjs/datetime'
import {Switch} from '@blueprintjs/core'
import {Close} from '@material-ui/icons'
import {Checkbox, FormControlLabel} from '@material-ui/core'
import {ChangeLogFieldSection, ChangeLogFlexLayoutTabStore, ChangeLogMenuItemElement, ChangeLogWorkspaceItems, ContentTitleBarItemField, ContentTitleBarItemFieldList, SearchPopover, useChangeLogFlexLayoutTabStore, useVipLocalStore, VipLocalStore, ChangeLogFilterFieldConfig} from '@modules'
import {StoresInstance} from '@stores'
import {PrimaryIconButton} from 'centric-ui-components'
import {action, observable, runInAction} from 'mobx'
import moment from 'moment'
import * as css from './ChangeLogMenuItemSelected.css'
import {SortAscendingIcon, SortDescendingIcon} from '@stores/ui'
import {DragDropStore} from '@stores/dragDrop'

/// region Store

export const FilterDatePicker = observer<{ children?: any, dateFilter?: string, selected?: DateRange, onSelect?: (selectedRanges?: DateRange) => void }>(function FilterDatePicker({children, onSelect, selected}) {

  return (
    <div>
      <Popover2
        usePortal={false}
        position={'bottom'}
        interactionKind={'click'}
        content={<DateRangePicker value={selected}
                                  shortcuts={false}
                                  highlightCurrentDay
                                  onChange={onSelect}
                                  maxDate={new Date()}
        />}
      >{children}</Popover2>
    </div>
  )
})

interface ToggleSortProps {
  order?: string
  onClick: () => void
  ascText: string
  descText: string
}

const ToggleSort = observer<ToggleSortProps>(function ToggleSort({order, ascText, descText, onClick}) {
  useMenuItemSelectedStore()
  const toggleButtonText = `${ascText} \xa0 ${descText}`
  return (
    <Switch checked={order == 'desc'}
            className={css.sortSwitch}
            innerLabelChecked={toggleButtonText}
            innerLabel={toggleButtonText}
            onChange={onClick}/>
  )
})

const SortReorder = observer(function SortReorder() {
  const store = useMenuItemSelectedStore(),
    {dragHandlers: {onDragStart}, type} = store
  return <div draggable onDragStart={e => {onDragStart && onDragStart(e, type)}}>
    <PrimaryIconButton icon='drag-handle-vertical'/>
  </div>
})

const SortFieldItem = observer(function SortFieldItem() {
  const store = useMenuItemSelectedStore(),
    {field, searchText, onSearch} = store

  const text = <div className={css.sortField}>{field.label}{field.workspaceLabel && <div className={css.workspace}>{field.workspaceLabel}</div>}</div>
  return (
    <SearchPopover text={text} searchText={searchText} onSearch={onSearch}>
      <ContentTitleBarItemFieldList selectedField={field}/>
    </SearchPopover>
  )
})

export class MenuItemSelectedStore extends VipLocalStore {
  constructor(stores: StoresInstance, public rootStore: ChangeLogFlexLayoutTabStore, public props: Omit<MyProps, 'className'>) {
    super(stores)
  }

  @computed get dragHandlers() {
    let clone: HTMLDivElement

    const {catalog, dragDrop, rootStore: {moveSortItem}} = this
    return {
      onDragStart: (e: React.DragEvent, type: string) => {
        runInAction(() => {
          e.stopPropagation()

          e.dataTransfer.effectAllowed = 'move'
          e.dataTransfer.dropEffect = 'move'

          e.dataTransfer.setData('ChangeLogMenuItemSelected', type)

          const dragItem = {id: type, dragType: 'ChangeLogSort', target: 'ChangeLogMenuItemSelected', targetExtra: type}
          DragDropStore._dragItem = dragItem

          const dragNode = e.currentTarget.closest(`[data-type='ChangeLogMenuItemSelected']`) as HTMLDivElement,
            clone = dragNode.cloneNode(true) as HTMLDivElement,
            {width, height} = dragNode.getBoundingClientRect()

          clone.style.setProperty('border-radius', `5px`)
          clone.style.setProperty('width', `${width + 20}px`)
          clone.style.setProperty('height', `${height + 20}px`)
          clone.style.setProperty('padding', `10px`)
          clone.style.setProperty('background-color', `white`)

          document.body.appendChild(clone)
          clone.setAttribute('data-is-drag-placeholder', 'true')

          e.dataTransfer.setDragImage(clone, width, height)
        })
      },
      onDragEnter: (e: React.DragEvent) => {
        e.stopPropagation()
        e.preventDefault()
        e.currentTarget.setAttribute('drag-hover', 'true')
      },
      onDragLeave: (e: React.DragEvent) => {
        e.stopPropagation()
        e.preventDefault()
        e.currentTarget.removeAttribute('drag-hover')
      },
      onDragEnd: (e: React.DragEvent) => {
        this.stopDraggingItem()
      },
      onDragOver: (e: React.DragEvent) => {
        e.stopPropagation()
        e.preventDefault()
        // this.stopDraggingItem()
      },
      onDrop: (e: React.DragEvent, type: string) => {
        runInAction(() => {
          const a = DragDropStore._dragItem
          console.log(type)
          console.log(DragDropStore._dragItem?.targetExtra)
          e.currentTarget.removeAttribute('drag-hover')
          if (DragDropStore._dragItem?.target === 'ChangeLogMenuItemSelected' && DragDropStore._dragItem?.targetExtra) {
            moveSortItem({field: DragDropStore._dragItem.targetExtra, oldField: type})
          }
          this.stopDraggingItem()
        })
      }
    }
  }

  @action stopDraggingItem = () => {
    this.dragDrop.reset()
  }

  @computed get feature() {
    return this.props.feature
  }

  @computed get field() {
    const {props} = this
    return props.field
  }

  @computed get searchText() {
    return this.props.searchText
  }

  @action onSearch = (event) => {
    this.props.onSearch(event)
  }

  @computed get defType() {
    const {field} = this
    return field?.def?.type ?? 'String'
  }

  @computed get type() {
    const {field} = this
    return field.field ?? field.type
  }

  @computed get label() {
    const {props, catalog, field} = this
    return props.label ?? catalog.labelForField(field) ?? field.label
  }

  @computed get key(): string {
    const {field: {workspace, field, type}} = this,
      f = field ?? type
    return workspace ? `${workspace}.${f}` : f
  }

  @computed get selected() {
    const {type, feature, field, rootStore: {filters, sortKeyByType}} = this

    return feature === 'filter' ? field.section ? filters[type]?.subSelected : filters[type]?.selected : sortKeyByType[type] ?? {}
  }

  @computed get excluded() {
    const {type, rootStore: {filters}} = this
    return filters[type]?.excluded
  }

  @computed get additionalFields() {
    const {rootStore: {config}} = this
    return config.additional
  }

  @action onChange = (selected: any, isDelete: boolean = false) => {
    const {type, field, rootStore: {onChangeFilters}} = this
    onChangeFilters({type, selected, field, isDelete})
  }

  @action onChangeSubSelection = (subSelected: any, isDelete: boolean = false) => {
    const {type, field, rootStore: {onChangeSubSelection}} = this
    onChangeSubSelection({type, subSelected, field, isDelete})
  }

  @action onExclude = (excluded: any, isDelete: boolean = false) => {
    const {type, field, rootStore: {onExcludeFilters}} = this
    onExcludeFilters({type, excluded, isDelete, field})
  }

  @computed get filterValueComponent() {
    const {type, defType, selected, onChange, field, onExclude, excluded, label} = this

    switch (type) {
      case 'datetime': {
        return <div className={css.date}>
          <FilterDatePicker>
            <div className={css.filterDate}>
              {
                field.values.map((d, i) => <div key={d}>{d} date: <span data-type={'date-selected'}>{(selected?.[i] ? moment(new Date(selected[i])).format('MMM DD, YYYY') : 'Not Set').toString()}</span></div>)
              }
            </div>
          </FilterDatePicker>
        </div>
      }
      case 'operation': {
        const sel = (selected ?? []).filter((s: string) => (s !== 'Inner Change' && s !== 'Created' && s !== 'Removed')),
          text = sel.length ? `${sel.filter((s: string, i: number) => i < 2).join(', ')}${sel?.length > 2 ? '...' : ''}` : `Select ${label}`
        return (
          <SearchPopover hideSearch onSearch={() => {}} searchText={''} text={text} selectedEl={(selected ?? []).length > 0 && selected.filter(s => field.values.includes(s)).map((value) => {
            const val: any = {id: value, name: value}
            return <ContentTitleBarItemField key={val.id}
                                             field={{...field, label: val.name, value: {name: val.name, id: val.id}, workspaceLabel: ''}}
                                             isSelected
                                             onSelectMenuItem={() => {onChange(value)}}
                                             hasExclude
                                             renderTo={'applied'}
                                             isExcluded={(excluded?.value ?? []).some(e => e.id === val.id)}
                                             onExcludeMenuItem={onExclude}
                                             searchText={''}/>
          })}>
            {field.values.map((value) => {
              const val: any = {id: value, name: value}
              return <ContentTitleBarItemField key={val.id}
                                               field={{...field, label: val.name, value: {name: val.name, id: val.id}, workspaceLabel: ''}}
                                               isSelected={(selected ?? []).some(s => s === val.id)}
                                               onSelectMenuItem={() => {onChange(value)}}
                                               hasExclude
                                               renderTo={'all'}
                                               isExcluded={(excluded?.value ?? []).some(e => e.id === val.id)}
                                               onExcludeMenuItem={onExclude}
                                               searchText={''}/>
            })}
          </SearchPopover>
        )
      }
      case 'workspace': {
        return (
          <ChangeLogWorkspaceItems field={field}
                                   onChange={onChange}
                                   selected={selected}/>
        )
      }
    }
    switch (defType) {
      case 'Int':
      case 'String':
      case 'Double':
      case 'Float':
      case 'Boolean':
      case 'Tag':
      case 'TagList':
      case 'EnumList':
      case 'Enum': {
        return <ChangeLogMenuItemElement onChange={onChange} field={field} selected={selected} onExclude={onExclude} excluded={excluded}/>
      }
      case 'DateTime':
        break
      case 'RefList':
        break
      case 'Ref':
        break
    }
    return <div>{type}</div>
  }

  @action toggleSort = () => {
    const {type, rootStore: {toggleSort}} = this
    toggleSort({type})
  }

  @computed get order() {
    const {rootStore: {sortKeyByType}, type} = this
    return sortKeyByType[type]?.order ?? 'desc'
  }

  @computed get sortToggleComponent() {
    const {toggleSort, order, type, defType} = this

    let ascText = 'A-Z',
      descText = 'Z-A'

    switch (type) {
      case 'datetime': {
        ascText = 'New - Old'
        descText = 'Old - New'
        break
      }
    }
    switch (defType) {
      case 'Boolean': {
        ascText = 'Yes - No'
        descText = 'No - Yes'
        break
      }
      case 'Int':
      case 'Double':
      case 'Float': {
        ascText = 'Low-High'
        descText = 'High-Low'
        break
      }
      case 'DateTime': {
        ascText = 'New - Old'
        descText = 'Old - New'
        break
      }
    }

    const beforeContent = {'--data-before-content': `${order == 'asc' ? `'${ascText}'` : `'${descText}'`}`}
    return (<div style={beforeContent as any}>
      <ToggleSort order={order} ascText={ascText} descText={descText} onClick={toggleSort}/>
    </div>)
  }

  @action onCancelFilterOption = () => {
    const {key, field, props: {onSelectMenuItem}, onChange} = this
    onSelectMenuItem(key, field)
    onChange([], true)
  }

  @observable workspaceSearchText: string = ''

  @action onSearchWorkspace = (e) => {
    this.workspaceSearchText = e.target.value
  }

  @computed get selectedEl() {
    const {field, label, onCancelFilterOption} = this
    return (
      <div className={css.filterItems}>
        {/*<PrimaryIconButton data-type={'close-icon'} icon={<Close/>} onClick={onCancelFilterOption}/>*/}
        <Close onClick={onCancelFilterOption} data-type={'close-icon'}/>
        <div data-type={'title'}>{label}</div>
        {field.workspaceLabel ? <div className={css.workspaceWrapper}>
          <div className={css.workspace}>{field.workspaceLabel}</div>
        </div> : null}
      </div>
    )
  }

  @computed get filterComponent() {
    const {field, selectedEl, filterValueComponent, selected, searchText, onSearch, onChangeSubSelection} = this

    return (
      <div className={css.container}>
        <SearchPopover onSearch={onSearch} searchText={searchText} text={selectedEl} btnClassName={css.menuButton}>
          <ContentTitleBarItemFieldList selectedField={field}/>
        </SearchPopover>
        {field.section ? <ChangeLogFieldSection onChangeSubSelection={onChangeSubSelection} field={field} selected={selected}/> : <div className={css.empty}/>}
        <div className={css.menuItem}>
          {filterValueComponent}
        </div>
      </div>
    )
  }

  @computed get sortComponent() {
    const {
      key, props: {onSelectMenuItem}, field, sortToggleComponent, type,
      dragHandlers: {onDrop, onDragOver, onDragEnter, onDragLeave}
    } = this

    return (
      <div className={classNames(css.container, css.sortContainer)}
           draggable
           onDrop={e => {onDrop && onDrop(e, type)}}
           onDragOver={e => {onDragOver && onDragOver(e)}}
           onDragEnter={e => {onDragEnter && onDragEnter(e)}}
           onDragLeave={e => {onDragLeave && onDragLeave(e)}}
      >
        <PrimaryIconButton className={css.remove} icon={<Close/>} onClick={() => onSelectMenuItem(key, field)}/>
        <SortFieldItem/>
        <div className={css.menuItem} data-type={'sort-icon'}>
          {sortToggleComponent}
        </div>
        {/*<SortReorder/>*/}
      </div>
    )
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = []

    return () => {
      _toDispose.forEach(d => d())
    }
  }
}

export const MenuItemSelectedStoreContext = React.createContext<MenuItemSelectedStore>(null)
export const useMenuItemSelectedStore = () => React.useContext(MenuItemSelectedStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
  field: ChangeLogFilterFieldConfig
  label?: string
  onSelectMenuItem: (key, field: ChangeLogFilterFieldConfig) => void
  feature?: 'filter' | 'sort'
  index: number
  searchText: string
  onSearch: (e) => void
}

export const ChangeLogMenuItemSelected = observer<MyProps>(function MenuItemSelected({className, ...props}) {
  const rootStore = useChangeLogFlexLayoutTabStore(),
    obsProps = useAsObservableSource({...props}),
    store = useVipLocalStore((stores) => new MenuItemSelectedStore(stores, rootStore, obsProps)),
    {feature, props: {index}} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <MenuItemSelectedStoreContext.Provider value={store}>
      <div data-index={index} data-type='ChangeLogMenuItemSelected' className={classNames(css.root, className)}>
        {feature === 'filter' ? store.filterComponent : store.sortComponent}
      </div>
    </MenuItemSelectedStoreContext.Provider>)
})

/// endregion





// WEBPACK FOOTER //
// ./src/modules/changelog/ChangeLogMenuItemSelected.tsx